import type { OptionGroupsData, PublicBundle, PublicOptionGroup, PublicProduct, PublicVariant } from '../graphql/public'

// Pure catalog logic — no React, no Apollo. The mapping rules mirror the kiosk
// (../kiosk/src/ui/ProductCard.tsx + i18n/locales/en.json) as the API contract
// requires; when the kiosk changes one of these labels, the board follows.

export const SPICE_LABEL = ['', 'Mild', 'Spicy', 'Extra spicy'] as const

export const DIETARY_LABEL: Record<string, string> = {
  VEGAN: 'Vegan',
  VEGETARIAN: 'Vegetarian',
  HALAL: 'Halal',
}

export const PROMO_LABEL: Record<string, string> = {
  POPULAR: 'Popular',
  NEW: 'New',
  CHEFS_SPECIAL: "Chef's special",
}

const DIETARY_ORDER = ['VEGAN', 'VEGETARIAN', 'HALAL']
const PROMO_ORDER = ['POPULAR', 'NEW', 'CHEFS_SPECIAL']

function attributes(p: PublicProduct): string[] {
  const a = p.attributes_json
  if (!a || !a.attributes) return []
  return a.attributes
}

/**
 * 0 = no marker, 1 "Mild", 2 "Spicy", 3 "Extra spicy". `spice_level` wins when it is
 * a positive number; otherwise the attribute flags decide — SPICY1 **and MILD** both
 * mean 1, exactly as the kiosk reads them.
 */
export function spiceLevel(p: PublicProduct): number {
  const lvl = p.attributes_json ? p.attributes_json.spice_level : null
  if (typeof lvl === 'number' && lvl > 0) return Math.min(3, lvl)
  const a = attributes(p)
  if (a.indexOf('SPICY3') !== -1) return 3
  if (a.indexOf('SPICY2') !== -1) return 2
  if (a.indexOf('SPICY1') !== -1 || a.indexOf('MILD') !== -1) return 1
  return 0
}

/** The first of VEGAN / VEGETARIAN / HALAL, or undefined. */
export function dietaryOf(p: PublicProduct): string | undefined {
  const a = attributes(p)
  return DIETARY_ORDER.filter((k) => a.indexOf(k) !== -1)[0]
}

/** The first of POPULAR / NEW / CHEFS_SPECIAL, or undefined. */
export function promoOf(p: PublicProduct): string | undefined {
  const a = attributes(p)
  return PROMO_ORDER.filter((k) => a.indexOf(k) !== -1)[0]
}

/** `995` → `9,95` — the kiosk's comma-decimal price, without the euro sign (owner's call, 2026-09-15). */
export function formatEuro(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',')
}

export function activeVariants(p: PublicProduct): PublicVariant[] {
  const vs = p.variants || []
  return vs
    .filter((v) => v.active)
    .slice()
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || a.id - b.id)
}

/** Products of one category (by name — ids differ per environment), in menu order. */
export function productsInCategory(products: PublicProduct[], categoryName: string): PublicProduct[] {
  return products
    .filter((p) => (p.categories || []).some((c) => c.name === categoryName))
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || a.name.localeCompare(b.name))
}

/** The column headers of a list: the active variant names of its first product. */
export function variantColumns(products: PublicProduct[]): string[] {
  if (products.length === 0) return []
  return activeVariants(products[0]).map((v) => v.name)
}

/**
 * A list price is a *from* price when the dish can cost more than it: any option
 * of the variant with a positive `price_delta_cents` (production 2026-11-05: "Only
 * Curry" +50 over "50/50 Curry & dal", extra coriander / chilli flakes +25). The
 * data cannot tell a style of dish from an add-on — "Coriander" is a required
 * No / Yes / Extra choice just like "Curry Style" — so the rule is the honest
 * minimum: the number on the wall is the least the customer can pay. Negative and
 * zero deltas never qualify.
 */
export function isFromPrice(groups: PublicOptionGroup[] | null | undefined): boolean {
  if (!groups) return false
  for (const g of groups) {
    const options = g.options || []
    for (const o of options) if (o.price_delta_cents > 0) return true
  }
  return false
}

/** The variant ids whose list price is a *from* price, out of an aliased `v<id>` answer. */
export function fromPriceIds(data: OptionGroupsData | null | undefined): number[] {
  if (!data) return []
  const ids: number[] = []
  for (const key in data) {
    if (key.charAt(0) !== 'v') continue
    const id = Number(key.slice(1))
    if (!isNaN(id) && isFromPrice(data[key])) ids.push(id)
  }
  return ids.sort((a, b) => a - b)
}

/** Active variant ids of every product in the given categories, in menu order, no duplicates. */
export function variantIdsIn(products: PublicProduct[], categoryNames: string[]): number[] {
  const ids: number[] = []
  for (const name of categoryNames) {
    for (const p of productsInCategory(products, name)) {
      for (const v of activeVariants(p)) if (ids.indexOf(v.id) === -1) ids.push(v.id)
    }
  }
  return ids
}

export interface DrinkGroup {
  kind: 'group'
  price_cents: number
  deposit_cents: number
  /** The group's price is a *from* price (every member's is — it is part of the key). */
  from: boolean
  products: PublicProduct[]
}
export interface DrinkSingle {
  kind: 'single'
  product: PublicProduct
}
export type DrinkLine = DrinkGroup | DrinkSingle

/**
 * Drinks compress by price: everything at the same (price, deposit) is one product
 * decision for the customer. A group of 3 or more lists its names on one line under
 * a single price; anything smaller stays a normal row. This is what makes the whole
 * drinks column fit without rotating it away (lessons.md). A *from* price is part of
 * the key: a drink that can cost more never shares a line with one that cannot.
 */
export function groupDrinks(
  products: PublicProduct[],
  isFrom: (variantId: number) => boolean = () => false,
): DrinkLine[] {
  const keys: string[] = []
  const buckets: Record<string, PublicProduct[]> = {}
  for (const p of products) {
    const v = activeVariants(p)[0]
    if (!v) continue
    const key = v.price_cents + '|' + v.deposit_cents + '|' + (isFrom(v.id) ? 1 : 0)
    if (!buckets[key]) {
      buckets[key] = []
      keys.push(key)
    }
    buckets[key].push(p)
  }
  // Biggest group first, then cheapest — the compressed line leads the column.
  const sorted = keys.slice().sort((a, b) => {
    const d = buckets[b].length - buckets[a].length
    if (d !== 0) return d
    return Number(a.split('|')[0]) - Number(b.split('|')[0])
  })
  const lines: DrinkLine[] = []
  for (const key of sorted) {
    const group = buckets[key]
    const parts = key.split('|')
    if (group.length >= 3) {
      lines.push({
        kind: 'group',
        price_cents: Number(parts[0]),
        deposit_cents: Number(parts[1]),
        from: parts[2] === '1',
        products: group,
      })
    } else {
      for (const p of group) lines.push({ kind: 'single', product: p })
    }
  }
  return lines
}

export interface HeroSlide {
  key: string
  image_url: string | null
  kicker: string
  title: string
  sub: string
  price: string
  /** The slide's price is a *from* price (isFromPrice); meaningless when `price` is empty. */
  from: boolean
}

function productSlide(p: PublicProduct, isFrom: (variantId: number) => boolean): HeroSlide {
  const promo = promoOf(p)
  const dietary = dietaryOf(p)
  const vs = activeVariants(p)
  return {
    key: 'p' + p.id,
    image_url: p.image_url,
    kicker: (promo && PROMO_LABEL[promo]) || (dietary && DIETARY_LABEL[dietary]) || '',
    title: p.name,
    sub: p.description || '',
    // Only single-variant items can show one honest price.
    price: vs.length === 1 ? formatEuro(vs[0].price_cents) : '',
    from: vs.length === 1 && isFrom(vs[0].id),
  }
}

function bundleSlide(b: PublicBundle): HeroSlide | null {
  const bp = b.bundle_product
  if (!bp) return null
  const v = (bp.variants || [])[0]
  return {
    key: 'b' + bp.id,
    image_url: bp.image_url,
    kicker: bp.name,
    title: bp.description || bp.name,
    sub: '',
    price: v ? '+ ' + formatEuro(v.price_cents) : '',
    from: false,
  }
}

/**
 * The hero slides of one screen: the products of its main list tagged POPULAR or NEW,
 * in menu order, plus the Meal Deal on the curry screen. Never zero slides — an empty
 * hero panel is a black rectangle on the wall — so a list without a tagged product
 * falls back to its first product.
 */
export function heroSlides(
  products: PublicProduct[],
  bundle?: PublicBundle | null,
  isFrom: (variantId: number) => boolean = () => false,
): HeroSlide[] {
  const tagged = products.filter((p) => {
    const promo = promoOf(p)
    return promo === 'POPULAR' || promo === 'NEW'
  })
  const base = tagged.length > 0 ? tagged : products.slice(0, 1)
  const slides = base.map((p) => productSlide(p, isFrom))
  if (bundle) {
    const slide = bundleSlide(bundle)
    if (slide) slides.push(slide)
  }
  return slides
}
